function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from 'react';
import { MKPTab } from 'src/models';
import { fetchListNotification, updateNotification } from 'src/redux/actions';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { navigateToConvertEvent, navigateToCreatorCorner, navigateToHome, navigateToMarketplace, navigateToStaking, navigateToVestingSafe } from 'src/utils/navigate';
import Portal from '../Portal';
import Tooltip from './Tooltip';
import { CalloutPosition } from './types';

const noop = () => {};

const CalloutContext = /*#__PURE__*/React.createContext({
  show: noop,
  hide: noop,
  listNotification: [],
  currentCallout: undefined,
  resetCallout: noop
});

const CalloutProvider = ({
  children,
  className
}) => {
  const dispatch = useTypedDispatch();
  const [title, setTitle] = React.useState('');
  const [isDisplay, setIsDisplay] = React.useState(false);
  const [color, setColor] = React.useState();
  const [position, setPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });
  const [tipDirection, setTipDirection] = React.useState(CalloutPosition.BOTTOM);
  const isSignedIn = useTypedSelector(state => !!state.userProfile.user);
  const {
    notis: listNotification,
    callout: currentCallout,
    expPick = 20 // Default value 20 seconds

  } = useTypedSelector(state => state.listNotification.data) || {};

  const display = (title, position, direction, color) => {
    setPosition(position);
    setTitle(title);
    setIsDisplay(true);
    setTipDirection(direction);
    color && setColor(color);
  };

  const hide = React.useCallback(() => {
    setIsDisplay(false);
  }, []);
  React.useEffect(() => {
    dispatch(fetchListNotification());
  }, [dispatch, isSignedIn]);
  React.useEffect(() => {
    const refreshTime = (expPick + 1) * 1000; // We need increase 1 second to wait BE update new data.

    const interval = setInterval(() => {
      hide();
      dispatch(fetchListNotification());
    }, refreshTime);
    return () => {
      clearInterval(interval);
    };
  }, [dispatch, hide, expPick]);
  const rect = React.useMemo(() => {
    switch (tipDirection) {
      case CalloutPosition.TOP:
        return {
          width: position.width,
          height: 0,
          top: position.top,
          left: position.left
        };

      case CalloutPosition.BOTTOM:
        return {
          width: position.width,
          height: 0,
          top: position.top + position.height,
          left: position.left
        };

      case CalloutPosition.BOTTOM_LEFT:
        return {
          width: position.width,
          height: 0,
          top: position.top + position.height,
          left: position.left
        };

      case CalloutPosition.LEFT:
        return {
          width: 0,
          height: position.height,
          top: position.top,
          left: position.left
        };

      case CalloutPosition.RIGHT:
        return {
          width: 0,
          height: position.height,
          top: position.top,
          left: position.left + position.width
        };

      default:
        return {
          width: position.width,
          height: 0,
          top: position.top,
          left: position.left
        };
    }
  }, [position.height, position.left, position.top, position.width, tipDirection]);
  const resetCallout = React.useCallback((tabs, isMenuOnMobile) => {
    const checkTab = tab => {
      if (tabs instanceof Array) {
        return !!tab && tabs.includes(tab);
      } else {
        return tab === tabs;
      }
    };

    if (isMenuOnMobile || currentCallout !== null && currentCallout !== void 0 && currentCallout.tabEnum && checkTab(currentCallout === null || currentCallout === void 0 ? void 0 : currentCallout.tabEnum)) {
      hide();
      (currentCallout === null || currentCallout === void 0 ? void 0 : currentCallout.tabEnum) && dispatch(updateNotification(currentCallout.tabEnum));
    }
  }, [currentCallout === null || currentCallout === void 0 ? void 0 : currentCallout.tabEnum, dispatch, hide]);
  const show = React.useCallback(props => {
    const {
      title,
      position,
      direction,
      color
    } = props;
    display(title, position, direction, color);
  }, []);
  const value = React.useMemo(() => ({
    show,
    hide,
    listNotification,
    currentCallout,
    isCalloutDisplayed: isDisplay,
    resetCallout
  }), [currentCallout, hide, isDisplay, listNotification, resetCallout, show]);

  const onClickTooltip = () => {
    hide();
    currentCallout && dispatch(updateNotification(currentCallout === null || currentCallout === void 0 ? void 0 : currentCallout.tabEnum));

    switch (currentCallout === null || currentCallout === void 0 ? void 0 : currentCallout.tabEnum) {
      case MKPTab.Buy:
        navigateToMarketplace();
        break;

      case MKPTab.ConvertionProgram:
        navigateToConvertEvent();
        break;

      case MKPTab.CreatorCorner:
        navigateToCreatorCorner();
        break;

      case MKPTab.Dashboard:
        navigateToHome();
        break;

      case MKPTab.Staking:
        navigateToStaking();
        break;

      case MKPTab.VestingSafe:
        navigateToVestingSafe();
        break;

      default:
        break;
    }
  };

  return /*#__PURE__*/React.createElement(CalloutContext.Provider, {
    value: value
  }, children, isDisplay && /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(Tooltip, _extends({}, rect, {
    onClick: onClickTooltip,
    className: className,
    content: title,
    color: color,
    position: tipDirection
  }))));
};

export const useCallout = () => React.useContext(CalloutContext);
export default CalloutProvider;